import {Pinia} from 'pinia'
import {useWorkspaceStore} from '../../../stores/WorkspaceStore/useWorkspaceStore'
import {useWorkspaceApiStore} from '@toolify/client/src/stores/api/WorkspaceApiStore/useWorkspaceApiStore'
import {RouteLocationNormalized} from 'vue-router'
import {usePermissionApiStore} from '@toolify/client/src/stores/api/PermissionApiStore/usePermissionApiStore'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {KnownError} from '@toolify/server/src/middleware/ApiResponseMiddleware/types/KnownError'
import {ApiException} from '@toolify/server/src/middleware/ApiResponseMiddleware/errors/ApiException'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'
import {useCacheStore} from '@toolify/client/src/stores/CacheStore/useCacheStore'
import {
  IWorkspaceResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IWorkspaceResponseInstance'
import {LocalPreferenceType} from '@toolify/client/src/stores/LocalPreferenceStore/enum/LocalPreferenceType'
import {useLocalPreferenceStore} from '@toolify/client/src/stores/LocalPreferenceStore/useLocalPreferenceStore'
import {shouldSkipWorkspaceResolution} from './shouldSkipWorkspaceResolution'
import {getUnavailableWorkspaceRedirect} from './getUnavailableWorkspaceRedirect'
import {PermissionType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/PermissionType'
import {HTTPStatusCode} from '@toolify/server/src/middleware/ApiResponseMiddleware/types/HttpStatusCode'
import {i18n} from '@toolify/client/src/setup/i18n'

export function ensureWorkspaceRouterHookFactory(pinia: Pinia) {
  return async function ensureWorkspaceRouterHook(to: RouteLocationNormalized, from: RouteLocationNormalized) {
    const workspaceName = to.params.workspaceName

    if(shouldSkipWorkspaceResolution(to, from, useWorkspaceStore(pinia).currentWorkspace?.name)) {
      return
    }

    if(workspaceName) {
      const cacheStore = useCacheStore(pinia)
      const workspaceStore = useWorkspaceStore(pinia)
      const workspaceApiStore = useWorkspaceApiStore(pinia)
      const permissionApiStore = usePermissionApiStore(pinia)
      const messages = useMessages()
      const localPreferenceStore = useLocalPreferenceStore(pinia)
      const isCurrentWorkspaceStillAuthorized = workspaceName === workspaceStore.currentWorkspace?.name
        && workspaceStore.hasPermission(PermissionType.readWorkspace)
      if(isCurrentWorkspaceStillAuthorized) {
        void localPreferenceStore.set(LocalPreferenceType.LastWorkspaceName, workspaceName as string)
        return
      }
      try {
        let workspace = cacheStore.getCacheItem<IWorkspaceResponseInstance>(`workspace-by-name-${workspaceName}`)
        if(!workspace) {
          const response = await workspaceApiStore.getWorkspaceByName(workspaceName as string)
          cacheStore.setCacheItem(`workspace-by-name-${workspaceName}`, response.workspace)
          workspace = response.workspace
        }
        const {permissions} = await permissionApiStore.getUserPermissionMapByResource(
          ResourceType.Workspace,
          workspace.id,
        )
        const canReadWorkspace = permissions.some(permission => {
          return permission.type === PermissionType.readWorkspace && permission.value === true
        })
        if(!canReadWorkspace) {
          throw new ApiException(KnownError.NoPermissions, 'No sufficient permissions', HTTPStatusCode.Forbidden)
        }
        if(workspace.name !== workspaceName) {
          // return to the same url with the correct workspace name
          return {
            ...to,
            params: {
              ...to.params,
              workspaceName: workspace.name,
            },
          }
        }
        workspaceStore.currentWorkspace = workspace
        workspaceStore.setCurrentWorkspacePermissionMap(permissions)
        void localPreferenceStore.set(LocalPreferenceType.LastWorkspaceName, workspace.name)
      } catch(e) {
        if(e instanceof ApiException) {
          cacheStore.setCacheItem(`workspace-by-name-${workspaceName}`, null)
          workspaceStore.currentWorkspace = null
          messages.warning(e.name === KnownError.NotFound
            ? i18n.global.t('workspaces.workspaceNotFound')
            : i18n.global.t('workspaces.noAccessToWorkspace'))
          return getUnavailableWorkspaceRedirect(workspaceName as string)
        }
        throw e
      }
    }
  }
}
